import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DocumentScanIllustration } from '../../components/public/DocumentScanIllustration.js';
import { PrimaryLogo } from '../../components/public/PrimaryLogo.js';
import { PublicFooter } from '../../components/public/PublicFooter.js';
import { PublicNav } from '../../components/public/PublicNav.js';
import { SecurityTrustIllustration } from '../../components/public/SecurityTrustIllustration.js';
import { SurgicalTechIllustration } from '../../components/public/SurgicalTechIllustration.js';

export function LandingPage() {
  useEffect(() => {
    document.title = 'ScrubLedger — Clinical case tracking for surgical technology programs';
  }, []);

  return (
    <div className="public-page">
      <PublicNav />
      <main>
        <section className="public-hero">
          <div className="public-hero__inner">
            <div className="public-hero__visual">
              <PrimaryLogo variant="png" displayWidth={280} loading="eager" className="public-brand-plate" />
            </div>
            <div className="public-hero__content">
              <p className="public-hero__eyebrow">Surgical Technology programs</p>
              <h1>Clinical case tracking without the paperwork.</h1>
              <p className="public-hero__lead">
                Students capture and submit clinical documentation while programs organize case progress,
                faculty review workflows, and graduation requirements in one secure workspace.
              </p>
              <div className="public-hero__actions">
                <Link to="/request-demo" className="btn btn--primary btn--lg">Request a Demo</Link>
                <Link to="/login" className="btn btn--secondary btn--lg">Sign In</Link>
              </div>
            </div>
          </div>
        </section>

        <section className="public-section public-section--alt" id="problem">
          <div className="public-section__inner">
            <h2>From paper chaos to structured clinical records</h2>
            <div className="public-compare">
              <article className="public-compare-card public-compare-card--before">
                <header className="public-compare-card__header">
                  <span className="public-compare-card__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="22" height="22" focusable="false">
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.5 4.5h5l2.75 2.75v11.25H4.5V4.5z"
                      />
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8.5 4.5v2.75h2.75"
                      />
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8.5 7h5l2.75 2.75v11H8.5V7z"
                      />
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12.5 7v2.75h2.75"
                      />
                    </svg>
                  </span>
                  <h3>The familiar friction</h3>
                </header>
                <ul className="public-compare-list public-compare-list--friction">
                  <li>Handwritten clinical and preceptor sheets that are easy to misplace</li>
                  <li>Manual case tallies and spreadsheet side-tracks</li>
                  <li>Instructor filing, storage, and retrieval across cohorts</li>
                  <li>Tracking specialty and case requirements by hand</li>
                  <li>Time spent verifying whether students are on pace to graduate</li>
                </ul>
              </article>
              <div className="public-compare-connector" aria-hidden="true">
                <span className="public-compare-connector__ring">
                  <svg viewBox="0 0 24 24" width="18" height="18" focusable="false">
                    <path
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 12h12M13 7l5 5-5 5"
                    />
                  </svg>
                </span>
              </div>
              <article className="public-compare-card public-compare-card--after">
                <header className="public-compare-card__header">
                  <span className="public-compare-card__icon public-compare-card__icon--after" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="22" height="22" focusable="false">
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 3.5h5.5L17 7v13.5H9V3.5z"
                      />
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M14.5 3.5V7h2.5"
                      />
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M10 13.5l2 2.5 3.5-4.25"
                      />
                    </svg>
                  </span>
                  <h3>How ScrubLedger helps</h3>
                </header>
                <ul className="public-compare-list public-compare-list--solution">
                  <li>Digital capture and upload of clinical documentation</li>
                  <li>Structured case extraction with faculty review</li>
                  <li>Faculty approval workflows with clear accountability</li>
                  <li>Requirement progress tied to verified academic cases</li>
                  <li>Program-wide visibility for directors and instructors</li>
                </ul>
              </article>
            </div>
          </div>
        </section>

        <section className="public-section public-section--tint" id="how-it-works">
          <div className="public-section__inner public-feature-row">
            <div className="public-feature-row__copy">
              <h2>How ScrubLedger works</h2>
              <p className="public-feature-row__lead">
                Capture clinical sheets, scan and extract case details, then review before verified cases
                enter the academic ledger.
              </p>
              <ol className="public-pipeline">
                <li><strong>Capture</strong> — upload or photograph clinical documentation</li>
                <li><strong>Scan &amp; extract</strong> — structured case fields for faculty review</li>
                <li><strong>Review</strong> — approve, correct, and track requirement progress</li>
              </ol>
            </div>
            <div className="public-feature-row__visual">
              <DocumentScanIllustration displayWidth={300} className="public-brand-plate public-brand-plate--scan" />
            </div>
          </div>
        </section>

        <section className="public-section" id="features">
          <div className="public-section__inner">
            <h2>Built for students and faculty</h2>
            <div className="public-cards public-cards--workflow">
              <article className="public-workflow-card public-workflow-card--student">
                <header className="public-workflow-card__header">
                  <span className="public-workflow-card__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="22" height="22" focusable="false">
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 3.5h5.5L17 7v13.5H9V3.5z"
                      />
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M14.5 3.5V7h2.5"
                      />
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 14.5V9.5M9.75 12.25L12 9.75l2.25 2.5"
                      />
                    </svg>
                  </span>
                  <h3>Student workflow</h3>
                </header>
                <ol className="public-workflow-steps">
                  <li>Capture or upload clinical documentation</li>
                  <li>Review extracted case information before submission</li>
                  <li>Submit for faculty review</li>
                  <li>Track progress toward program requirements</li>
                </ol>
              </article>
              <article className="public-workflow-card public-workflow-card--faculty">
                <header className="public-workflow-card__header">
                  <span className="public-workflow-card__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="22" height="22" focusable="false">
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M7 3.5h10v2.5H7V3.5z"
                      />
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M7 6.5h10v12H7V6.5z"
                      />
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9.5 14l2 2.5 3.5-4.25"
                      />
                    </svg>
                  </span>
                  <h3>Instructor &amp; program director workflow</h3>
                </header>
                <ol className="public-workflow-steps">
                  <li>Review submitted cases in a centralized queue</li>
                  <li>Approve or correct extracted information</li>
                  <li>Monitor student progress and missing requirements</li>
                  <li>Manage program configuration and clinical workflow</li>
                </ol>
              </article>
            </div>
          </div>
        </section>

        <section className="public-section public-section--tint">
          <div className="public-section__inner public-feature-row public-feature-row--reverse">
            <div className="public-feature-row__copy">
              <h2>Built for Surgical Technology Programs</h2>
              <p className="public-feature-row__lead">
                From clinical experience to verified case progress — workflows shaped for surgical technology
                cohorts, preceptor documentation, and program director oversight.
              </p>
            </div>
            <div className="public-feature-row__visual">
              <SurgicalTechIllustration variant="png" displayWidth={300} className="public-brand-plate" />
            </div>
          </div>
        </section>

        <section className="public-section public-section--alt">
          <div className="public-section__inner">
            <h2>Core capabilities</h2>
            <ul className="public-cap-grid" role="list">
              <li className="public-cap-card">
                <span className="public-cap-card__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="22" height="22" focusable="false">
                    <path
                      fill="currentColor"
                      d="M4 4h12v16H4V4zm14 2h2v14h-2V6zm-4 4H8v2h6V10zm0 4H8v2h6v-2z"
                    />
                  </svg>
                </span>
                <p>Clinical document capture and upload</p>
              </li>
              <li className="public-cap-card">
                <span className="public-cap-card__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="22" height="22" focusable="false">
                    <path
                      fill="currentColor"
                      d="M9 3h6l5 5v13H4V3h5zm1 2v4h8V8l-3-3h-5zm-2 8h10v2H8v-2zm0 4h7v2H8v-2z"
                    />
                  </svg>
                </span>
                <p>Case extraction and faculty review workflow</p>
              </li>
              <li className="public-cap-card">
                <span className="public-cap-card__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="22" height="22" focusable="false">
                    <path
                      fill="currentColor"
                      d="M5 4h14v2H5V4zm0 5h14v11H5V9zm3 3h8v2H8v-2zm0 4h5v2H8v-2z"
                    />
                  </svg>
                </span>
                <p>Academic ledger for verified cases</p>
              </li>
              <li className="public-cap-card">
                <span className="public-cap-card__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="22" height="22" focusable="false">
                    <path
                      fill="currentColor"
                      d="M4 6h16v2H4V6zm2 4h12v10H6V10zm2 2v2h4v-2H8zm0 4v2h8v-2H8z"
                    />
                  </svg>
                </span>
                <p>Requirement progress tracking</p>
              </li>
              <li className="public-cap-card">
                <span className="public-cap-card__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="22" height="22" focusable="false">
                    <path
                      fill="currentColor"
                      d="M8 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm8 1a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM4 18c0-2.2 3.6-4 8-4s8 1.8 8 4v2H4v-2z"
                    />
                  </svg>
                </span>
                <p>Multi-user program workflows</p>
              </li>
              <li className="public-cap-card">
                <span className="public-cap-card__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="22" height="22" focusable="false">
                    <path
                      fill="currentColor"
                      d="M12 2 4 6v6c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V6l-8-4zm0 3.2 5 2.5V12c0 3.7-2.4 7.2-5 8.3-2.6-1.1-5-4.6-5-8.3V7.7l5-2.5zm0 4.85 4 4-1.4 1.4-2.6-3.1-2.6 2.6-1.4-1.4 4-3.5z"
                    />
                  </svg>
                </span>
                <p>Secure tenant separation between programs</p>
              </li>
            </ul>
          </div>
        </section>

        <section className="public-section">
          <div className="public-section__inner public-trust">
            <div className="public-trust__copy">
              <h2>Security &amp; trust</h2>
              <p>
                ScrubLedger is designed for authenticated access, tenant-isolated program data, controlled
                role-based access, and private document storage architecture. We do not claim third-party
                compliance certifications on this page.
              </p>
            </div>
            <div className="public-trust__visual">
              <SecurityTrustIllustration className="public-trust-illustration" />
            </div>
          </div>
        </section>

        <section className="public-cta-band">
          <div className="public-cta-band__inner">
            <h2>See ScrubLedger with your program</h2>
            <p>Request a guided walkthrough or sign in to your existing program workspace.</p>
            <div className="public-hero__actions public-hero__actions--center">
              <Link to="/request-demo" className="btn btn--primary btn--lg">Request a Demo</Link>
              <Link to="/login" className="btn btn--secondary btn--lg">Sign In</Link>
            </div>
          </div>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
